import type { VocabularyResponse, VocabularyTerm } from "@hermes/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { useState, type FormEvent } from "react";
import { ApiError } from "../../api/http";
import { vocabularyApi, type VocabularyScope } from "../../api/vocabulary";
import { RetryIcon, TrashIcon } from "../../ui/icons";
import { IconButton } from "../../ui/IconButton";
import { Spinner } from "../../ui/Spinner";
import { toast } from "../../ui/toast";
import { useMe } from "../chat/queries";
import { Link } from "@tanstack/react-router";
import { setUi } from "../../lib/uiStore";
import { Intro } from "./Intro";
import { Section } from "./Section";

const ease = [0.22, 1, 0.36, 1] as const;
export const VOCABULARY_KEY = ["vocabulary"];
const KEY = VOCABULARY_KEY;

function vocabularyError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "duplicate") return "Это слово уже есть в словаре.";
    if (err.code === "limit_reached") return `В словаре уже ${String(err.body.limit ?? "")} слов. Удалите ненужные, чтобы добавить новое.`;
    if (err.status === 400) return "Нужна хотя бы одна буква, до 60 символов. Вариантов — не больше 10.";
  }
  return "Не удалось сохранить. Попробуйте ещё раз.";
}

/**
 * Словарь распознавания речи: фамилии, названия, сокращения. По нему сервер поправляет диктовку,
 * голосовые в Telegram и расшифровки совещаний. Общий словарь компании видят все, правит администратор.
 */
export function VocabularySection() {
  const me = useMe();
  const q = useQuery({ queryKey: KEY, queryFn: vocabularyApi.get, staleTime: 0 });
  const admin = me?.role === "admin";

  if (q.isPending)
    return (
      <div className="space-y-3 pt-1" aria-busy="true">
        {[60, 85, 70].map((w, i) => (
          <div key={i} className="h-11 skeleton rounded-control bg-field" style={{ width: `${w}%` }} />
        ))}
      </div>
    );
  if (q.isError)
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <p className="text-[15px] text-sub">Не удалось загрузить словарь.</p>
        <button
          type="button"
          onClick={() => void q.refetch()}
          className="pressable inline-flex h-11 items-center gap-2 rounded-control border border-line px-4 text-sm font-semibold hover:bg-bg"
        >
          <RetryIcon className="size-4" />
          Повторить
        </button>
      </div>
    );

  return (
    <>
      <Intro chapter="voice">Фамилии, названия и сокращения, которые распознавание пишет неправильно.</Intro>
      <Section title="Мой словарь" hint="Виден только вам.">
        <TermEditor scope="personal" terms={q.data.personal} editable />
      </Section>
      <Section title="Общий словарь компании" hint="Действует у всех сотрудников, ведёт администратор.">
        {admin && (
          <p className="-mt-1 mb-3 text-[13px] leading-[18px] text-sub">
            Добавлять и удалять общие слова —{" "}
            <Link
              to="/admin/vocabulary"
              search={{ days: 30 }}
              onClick={() => setUi({ settings: null })}
              className="font-semibold text-accent-ink underline decoration-accent-line underline-offset-2 hover:decoration-current"
            >
              в админке, раздел «Словарь»
            </Link>
            .
          </p>
        )}
        <TermEditor scope="company" terms={q.data.company} editable={false} />
      </Section>
    </>
  );
}

export function TermEditor({ scope, terms, editable }: { scope: VocabularyScope; terms: VocabularyTerm[]; editable: boolean }) {
  const qc = useQueryClient();
  const [term, setTerm] = useState("");
  const [variants, setVariants] = useState("");
  const [error, setError] = useState("");
  const add = useMutation({
    mutationFn: () =>
      vocabularyApi.add(scope, {
        term,
        variants: variants
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
      }),
    onSuccess: ({ term: added }) => {
      qc.setQueryData<VocabularyResponse>(KEY, (v) => v && { ...v, [scope]: [...v[scope], added].sort((a, b) => a.term.localeCompare(b.term, "ru")) });
      setTerm("");
      setVariants("");
      toast(`«${added.term}» добавлено в словарь`);
    },
    onError: (err) => setError(vocabularyError(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => vocabularyApi.remove(scope, id),
    onSuccess: (_r, id) => qc.setQueryData<VocabularyResponse>(KEY, (v) => v && { ...v, [scope]: v[scope].filter((t) => t.id !== id) }),
    onError: (err) => {
      if (err instanceof ApiError && err.status === 404) return void qc.invalidateQueries({ queryKey: KEY });
      toast(vocabularyError(err));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (add.isPending) return;
    if (!term.trim()) return setError("Впишите слово так, как оно должно быть написано.");
    add.mutate();
  };
  const field =
    "h-11 w-full rounded-control border bg-surface px-3.5 text-base focus:outline-none focus-visible:outline-none border-line focus:border-accent focus:shadow-[0_0_0_4px_rgb(23_128_60/16%)]";
  const busyId = remove.isPending ? remove.variables : null;

  return (
    <>
      {editable && (
        <form onSubmit={submit} noValidate className="mb-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end" aria-label={scope === "company" ? "Добавить в общий словарь" : "Добавить в мой словарь"}>
          <label className="grid gap-1">
            <span className="text-[13px] font-medium text-ink-2">Как пишется</span>
            <input
              type="text"
              autoComplete="off"
              placeholder="Мегерамов"
              value={term}
              maxLength={60}
              onChange={(e) => {
                setTerm(e.target.value);
                setError("");
              }}
              aria-invalid={!!error}
              className={`${field} ${error ? "border-err" : ""}`}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-[13px] font-medium text-ink-2">Как слышится, если иначе</span>
            <input
              type="text"
              autoComplete="off"
              placeholder="магирамов, мега рамов"
              value={variants}
              onChange={(e) => {
                setVariants(e.target.value);
                setError("");
              }}
              className={field}
            />
          </label>
          <button
            type="submit"
            aria-disabled={add.isPending}
            className="pressable inline-flex h-11 items-center justify-center gap-2 rounded-control bg-accent-grad hover-accent-grad px-5 text-sm font-semibold text-white"
          >
            {add.isPending && <Spinner className="size-4" />}
            Добавить
          </button>
          {error && (
            <p role="alert" className="text-[13px] text-err sm:col-span-3">
              {error}
            </p>
          )}
        </form>
      )}
      {terms.length === 0 ? (
        <p className="text-[13px] text-sub">{scope === "company" ? "Общих слов пока нет." : "Слов пока нет."}</p>
      ) : (
        <ul className="overflow-hidden rounded-[16px] border border-edge">
          <AnimatePresence initial={false}>
            {terms.map((t) => (
              <motion.li
                key={t.id}
                layout
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.26, ease }}
                className="flex min-h-12 items-center gap-2 border-b border-line-soft py-1.5 pr-2 pl-4 last:border-b-0"
              >
                <p className="min-w-0 flex-1 py-1.5 text-sm leading-[20px] break-words">
                  <span className="font-semibold">{t.term}</span>
                  {t.variants.length > 0 && <span className="block text-xs text-sub">слышится как: {t.variants.join(", ")}</span>}
                </p>
                {editable && (
                  <IconButton label={`Удалить «${t.term}»`} size="sm" onClick={() => remove.mutate(t.id)} aria-disabled={busyId === t.id}>
                    {busyId === t.id ? <Spinner tone="dark" /> : <TrashIcon />}
                  </IconButton>
                )}
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      )}
    </>
  );
}
